import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { GENDER_LIST_ONBOARDING } from 'src/consumer/constants'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { prepareDataForUpdate } from '../Form/helpers/dataHelpers'
import { postData } from 'src/consumer/services/profileService'
import { isBasicOnboardingDone } from 'src/mentor/helpers/utilityFunctions'
import { isLoggedIn } from 'src/consumer/services/patientProspectService'
import { Container, Eyebrow, SectionTitle, Lede, Card } from '../AumhumSite/sections'

// /onboarding — the consumer-side basic-profile wizard for ROLE_USER-only
// accounts that haven't filled in name / age / gender / content language
// yet. Surfaced as a real route (not just an inside-modal step) so that
// every "Wellness app" entry point can funnel a half-finished user
// through the same flow consistently:
//
//   - The / consumer route lands here via RootLanding when the user
//     has only ROLE_USER and isBasicOnboardingDone(profileData) is false.
//   - The AuthenticatedTopBar portal switcher's "Wellness app" item
//     points at / and benefits from the same redirect.
//   - The CareStatusView wellness-escape link navigates to / after
//     opting the prospect record out — same redirect catches it.
//   - Any external "open the AUMHUM wellness app" deep-link from a
//     marketing email / push can target /onboarding directly to be
//     explicit about the wizard.
//
// On submit we PATCH /api/secure/user/update via the same payload
// helper the legacy in-modal flow uses, then route the user to the
// ?next= path (defaults to / for AppHomepage). When the user is
// already past basic onboarding we don't render the form — we just
// redirect, so a stale deep-link doesn't show the wizard to someone
// who's complete.

const DEFAULT_CONTENT_LANGUAGE = 'en'
const NAME_RE = /\S/
const AGE_MIN = 12
const AGE_MAX = 120

export default function OnboardingView() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // /wellness is the canonical wellness home now (the old / route
  // bounces ROLE_USER-only users here anyway, but using /wellness
  // explicitly keeps the URL stable and matches the namespaced home
  // /wellness/onboarding sits under).
  const next = searchParams.get('next') || '/wellness'

  const { profileData, profileDataLoading, refreshProfileData } = useContext(ProfileContext) || {}

  const initial = useMemo(() => deriveInitialFromProfile(profileData), [profileData])
  const [name, setName] = useState(initial.name)
  const [age, setAge] = useState(initial.age)
  const [gender, setGender] = useState(initial.gender)
  // Content language defaults to English. The wider funnel (care,
  // signup) is English-by-default — we keep the wellness onboarding
  // aligned with that until we have localised content for real.
  const [contentLanguage, setContentLanguage] = useState(initial.contentLanguage)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Refresh stored state whenever profileData lands (it may arrive
  // after this component mounts in some race conditions).
  useEffect(() => {
    if (!profileData) return
    setName((cur) => cur || (profileData?.firstName || ''))
    setGender((cur) => cur || (profileData?.gender || ''))
    setAge((cur) => cur || (deriveAgeFromDob(profileData?.dob) || ''))
    setContentLanguage((cur) =>
      cur || (profileData?.personalize?.studentPersonalize?.contentLanguages?.[0] || DEFAULT_CONTENT_LANGUAGE),
    )
  }, [profileData])

  // Anonymous visitors don't belong here — punt to /signup. Anyone
  // already past basic onboarding gets bounced to ?next= so the route
  // doesn't double-prompt a complete user.
  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/signup', { replace: true })
      return
    }
    if (profileDataLoading) return
    if (profileData && isBasicOnboardingDone(profileData)) {
      navigate(next, { replace: true })
    }
  }, [profileData, profileDataLoading, navigate, next])

  const validAge = age && /^\d+$/.test(String(age))
    && Number(age) >= AGE_MIN && Number(age) <= AGE_MAX
  const validName = NAME_RE.test(name || '')
  const validGender = !!(gender && GENDER_LIST_ONBOARDING.some((g) => g.value === gender))
  const canSubmit = validName && validAge && validGender && !submitting

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault()
    setError('')
    if (!validName) {
      setError('Please enter your name.')
      return
    }
    if (!validAge) {
      setError(`Please enter an age between ${AGE_MIN} and ${AGE_MAX}.`)
      return
    }
    if (!validGender) {
      setError('Please pick an option for gender.')
      return
    }
    setSubmitting(true)
    try {
      const payload = prepareDataForUpdate(
        {
          name: name.trim(),
          age: Number(age),
          gender,
          contentLanguages: [contentLanguage || DEFAULT_CONTENT_LANGUAGE],
        },
        profileData || {},
      )
      const res = await postData('/api/secure/user/update', payload)
      const httpStatus = res?.status ?? res?.response?.status
      if (httpStatus < 200 || httpStatus >= 300) {
        throw new Error(
          res?.response?.data?.message ||
          res?.data?.message ||
          'Could not save your details. Please try again.',
        )
      }
      // Refresh ProfileContext so the rest of the app sees the new
      // profile shape without a hard reload.
      if (typeof refreshProfileData === 'function') {
        try {
          await refreshProfileData({ _forceRefresh: true })
        } catch {
          // best-effort — navigation below will trigger another fetch
        }
      }
      navigate(next, { replace: true })
    } catch (err) {
      setError(err?.message || 'Could not save your details. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // Render a friendly stub while we figure out where to send the user.
  if (profileDataLoading && !profileData) {
    return (
      <Container className="pps-shell">
        <Eyebrow>Setting things up</Eyebrow>
        <SectionTitle as="h1">One moment…</SectionTitle>
        <Lede>Loading your profile so we can pick the right next step.</Lede>
      </Container>
    )
  }

  return (
    <Container className="pps-shell">
      <Eyebrow>Welcome to AUMHUM</Eyebrow>
      <SectionTitle as="h1">Tell us a bit about you</SectionTitle>
      <Lede>
        A quick one-time setup so the wellness app can personalise meditations,
        journals, and recommendations for you. Takes under a minute.
      </Lede>

      <Card className="pps-card-form" emphasised style={{ marginTop: '1.25rem' }}>
        <form onSubmit={handleSubmit} noValidate>
          <div className="pps-field">
            <label className="pps-field-label" htmlFor="onboarding-name">Your name</label>
            <input
              id="onboarding-name"
              className="pps-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="First name is fine"
              autoComplete="given-name"
              disabled={submitting}
            />
          </div>

          <div className="pps-field-row">
            <div className="pps-field pps-field--half">
              <label className="pps-field-label" htmlFor="onboarding-age">Age</label>
              <input
                id="onboarding-age"
                className="pps-input"
                type="number"
                min={AGE_MIN}
                max={AGE_MAX}
                value={age}
                onChange={(e) => setAge(e.target.value.replace(/\D/g, ''))}
                placeholder="e.g. 32"
                inputMode="numeric"
                disabled={submitting}
              />
            </div>

            <div className="pps-field pps-field--half">
              <span className="pps-field-label">Gender</span>
              <div className="pps-chip-row" role="radiogroup" aria-label="Gender">
                {GENDER_LIST_ONBOARDING.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={gender === opt.value}
                    className={`pps-chip pps-chip--small${gender === opt.value ? ' is-active' : ''}`}
                    onClick={() => setGender(opt.value)}
                    disabled={submitting}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="pps-field">
            <span className="pps-field-label">
              Preferred content language{' '}
              <span className="pps-field-optional">(more coming soon)</span>
            </span>
            <div className="pps-chip-row" role="radiogroup" aria-label="Content language">
              {/*
                English is the only fully-supported content language right
                now. Showing the chip rather than auto-setting it makes
                the choice visible — and future multi-language support
                only needs additional entries here.
              */}
              <button
                type="button"
                role="radio"
                aria-checked={contentLanguage === 'en'}
                className={`pps-chip${contentLanguage === 'en' ? ' is-active' : ''}`}
                onClick={() => setContentLanguage('en')}
                disabled={submitting}
              >
                English
              </button>
            </div>
          </div>

          {error ? (
            <div className="pps-error" role="alert">{error}</div>
          ) : null}

          <div className="pps-cta-row" style={{ marginTop: '1rem' }}>
            <button
              type="submit"
              className={`pps-btn is-primary${canSubmit ? '' : ' is-disabled'}`}
              disabled={!canSubmit}
            >
              {submitting ? 'Saving…' : 'Continue to AUMHUM'}
            </button>
          </div>
        </form>
      </Card>
    </Container>
  )
}

// ─── helpers ────────────────────────────────────────────────────────────────

function deriveInitialFromProfile(profileData) {
  if (!profileData) {
    return { name: '', age: '', gender: '', contentLanguage: DEFAULT_CONTENT_LANGUAGE }
  }
  return {
    name: profileData.firstName || '',
    age: deriveAgeFromDob(profileData.dob) || '',
    gender: profileData.gender || '',
    contentLanguage:
      profileData.personalize?.studentPersonalize?.contentLanguages?.[0] || DEFAULT_CONTENT_LANGUAGE,
  }
}

// Best-effort: DOB on Profile is a String. Backend has been persisting
// "yyyy-01-01" when the prospect form supplied just an age — we read the
// year part and compute approximate-age so the wizard pre-fills sensibly.
function deriveAgeFromDob(dob) {
  if (!dob || typeof dob !== 'string') return null
  const match = dob.match(/^(\d{4})/)
  if (!match) return null
  const year = Number(match[1])
  if (!Number.isFinite(year) || year < 1900 || year > new Date().getFullYear()) return null
  const age = new Date().getFullYear() - year
  if (age < 0 || age > 130) return null
  return age
}
